import React from "react";
import { Card, CardBody } from "reactstrap";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

/**
 * AM progress widget.
 * Expects `amStats` shaped like:
 * {
 *   totals: { meetings_count, actions_open, actions_done },
 *   by_am: [{ user_name, meetings_count, actions_open, actions_done }]
 * }
 */
export default function AmMeetingStats({ amStats, loading }) {
  const totals = amStats?.totals || {};
  const byAm = Array.isArray(amStats?.by_am) ? amStats.by_am : [];

  const chartData = byAm.map((row) => ({
    name: row.user_name || row.am_name || "AM",
    meetings: Number(row.meetings_count) || 0,
  }));

  if (loading) {
    return (
      <Card className="custom-card mb-0">
        <CardBody>
          <h5 className="fw-semibold mb-3">Account Management</h5>
          <p className="text-muted mb-0">Loading AM meeting stats...</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="custom-card mb-0">
      <CardBody>
        <h5 className="fw-semibold mb-3">Account Management</h5>

        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <div className="border rounded p-3 text-center">
              <div className="fs-24 fw-semibold">
                {totals.meetings_count ?? 0}
              </div>
              <div className="text-muted">Meetings logged</div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="border rounded p-3 text-center">
              <div className="fs-24 fw-semibold text-warning">
                {totals.actions_open ?? 0}
              </div>
              <div className="text-muted">Open actions</div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="border rounded p-3 text-center">
              <div className="fs-24 fw-semibold text-success">
                {totals.actions_done ?? 0}
              </div>
              <div className="text-muted">Completed actions</div>
            </div>
          </div>
        </div>

        {chartData.length > 0 ? (
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-25}
                  textAnchor="end"
                  interval={0}
                  height={60}
                />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="meetings" name="Meetings" fill="#2a3c7d" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-center text-muted mb-3">
            No AM meeting activity for this month.
          </p>
        )}

        {byAm.length > 0 && (
          <div className="table-responsive mt-3">
            <table className="table table-bordered table-sm align-middle text-center">
              <thead>
                <tr>
                  <th>Account Manager</th>
                  <th>Meetings</th>
                  <th>Open actions</th>
                  <th>Done actions</th>
                </tr>
              </thead>
              <tbody>
                {byAm.map((row, index) => (
                  <tr key={row._user_id || row.user_name || index}>
                    <td>{row.user_name || row.am_name || "-"}</td>
                    <td>{row.meetings_count ?? 0}</td>
                    <td>{row.actions_open ?? 0}</td>
                    <td>{row.actions_done ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
